import { useState, useEffect } from 'react';
import { Button, ButtonLink, Checkbox } from '@resideo/blueprint-oneportal';
import { closeIcon } from '../../assets/icons';
import styles from './EmployeeFilterPanel.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FilterState {
  roles: Set<string>;
  locationIds: Set<string>;
}

export interface LocationOption {
  id: string;
  name: string;
}

interface EmployeeFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  currentFilters: FilterState;
  availableLocations: LocationOption[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: 'ADMIN', label: 'Site Manager' },
  { value: 'MEMBER', label: 'Employee' },
  { value: 'GUEST', label: 'Guest' },
] as const;

// ─── Icons ────────────────────────────────────────────────────────────────────

function ChevronIcon({ expanded }: { expanded: boolean }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={`${styles.chevron} ${expanded ? styles.chevronUp : ''}`}
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EmployeeFilterPanel({
  isOpen,
  onClose,
  onApply,
  currentFilters,
  availableLocations,
}: EmployeeFilterPanelProps) {
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set(currentFilters.roles));
  const [selectedLocationIds, setSelectedLocationIds] = useState<Set<string>>(
    new Set(currentFilters.locationIds),
  );
  const [roleExpanded, setRoleExpanded] = useState(true);
  const [locationExpanded, setLocationExpanded] = useState(true);

  // Sync local state when panel opens or currentFilters change
  useEffect(() => {
    if (isOpen) {
      setSelectedRoles(new Set(currentFilters.roles));
      setSelectedLocationIds(new Set(currentFilters.locationIds));
    }
  }, [isOpen, currentFilters]);

  if (!isOpen) return null;

  function toggleRole(value: string) {
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }

  function toggleLocation(id: string) {
    setSelectedLocationIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleApply() {
    onApply({ roles: new Set(selectedRoles), locationIds: new Set(selectedLocationIds) });
    onClose();
  }

  function handleClearAll() {
    setSelectedRoles(new Set());
    setSelectedLocationIds(new Set());
  }

  return (
    <>
      {/* Backdrop */}
      <div className={styles.overlay} onClick={onClose} />

      {/* Panel */}
      <div className={styles.panel} role="dialog" aria-modal="true" aria-label="Filter employees">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.titleActions}>
            <h2 className={styles.title}>Filters</h2>
            <ButtonLink size="default" underline={false} onClick={handleClearAll}>
              Clear All
            </ButtonLink>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close filter panel"
          >
            <img src={closeIcon} width="16" height="16" alt="" aria-hidden="true" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className={styles.body}>
          {/* Role section */}
          <div className={styles.section}>
            <button
              type="button"
              className={styles.sectionHeader}
              onClick={() => setRoleExpanded(p => !p)}
            >
              <span className={styles.sectionTitle}>Role</span>
              <ChevronIcon expanded={roleExpanded} />
            </button>

            {roleExpanded && (
              <div className={styles.checkboxList}>
                {ROLE_OPTIONS.map(opt => (
                  <div key={opt.value} className={styles.checkboxRow}>
                    <Checkbox
                      checked={selectedRoles.has(opt.value)}
                      onChange={() => toggleRole(opt.value)}
                    />
                    <span
                      className={styles.checkboxLabel}
                      onClick={() => toggleRole(opt.value)}
                    >
                      {opt.label}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Divider */}
          <div className={styles.divider} />

          {/* Location section */}
          <div className={styles.section}>
            <button
              type="button"
              className={styles.sectionHeader}
              onClick={() => setLocationExpanded(p => !p)}
            >
              <span className={styles.sectionTitle}>Location</span>
              <ChevronIcon expanded={locationExpanded} />
            </button>

            {locationExpanded && (
              <div className={styles.checkboxList}>
                {availableLocations.length === 0 ? (
                  <span className={styles.emptyNote}>No locations available</span>
                ) : (
                  availableLocations.map(loc => (
                    <div key={loc.id} className={styles.checkboxRow}>
                      <Checkbox
                        checked={selectedLocationIds.has(loc.id)}
                        onChange={() => toggleLocation(loc.id)}
                      />
                      <span
                        className={styles.checkboxLabel}
                        onClick={() => toggleLocation(loc.id)}
                      >
                        {loc.name}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button variant="highlightTertiary" className={styles.footerButton} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className={styles.footerButton} onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </>
  );
}
